<template>
    <div
        id="lt-yantr"
        class="conforming-limits-tool">
        <div class="conforming-block">
            <h2>{{ headline }}</h2>
            <p class="content">
                {{ subHeadline }}
            </p>
        </div>
        <hr />
        <div class="conforming-block">
            <div class="state-select">
                <drop-down
                    v-model="State"
                    :options="StateList"
                    :set-selected="true"
                />
            </div>
            <div class="clear-fix"></div>
            <ul class="filter-inputs">
                <li>
                    <drop-down
                        v-model="County"
                        :label="countryLabel"
                        :options="CountyList"
                        :value="County"
                        :default-option="CountyOptionTitle"
                        :set-selected="true"
                    />
                </li>
                <li>
                    <label>{{ firstOutputTitle }}</label>
                    <span class="price-limit">{{ ConformingLimit }}</span>
                </li>
                <li>
                    <label>{{ secondOutputTitle }}</label>
                    <span class="price-limit">{{ FhaLimit }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import States from '~/constants/states';
    import DropDown from '~/components/drop-down.vue';
    import { GetIpAddress, GetZipCode } from '~/services/geo-location';
    import GraphQLMixin from '~/mixin/graphql';
    import LimitsForCounty from '~/services/graphql/LimitsForCounty.gql';
    import CountiesFromState from '~/services/graphql/CountiesFromState.gql';
    import { getFinancialApiUrl, setHeader } from '~/helpers/financialapi-url-env';

    export default {
        components: {
            DropDown,
        },
        mixins: [GraphQLMixin],
        props: {
            headline: {
                default: 'Find conforming and FHA limits in your area',
            },
            subHeadline: {
                default: 'Conforming loans meet terms and conditions determined by Fannie Mae and Freddie Mac. They include the maximum loan amount, requirements the borrower must meet to qualify for the loan and properties considered suitable for mortgages. Fannie Mae and Freddie Mac set loan limits by evaluating the average change in home price each year.',
            },
            firstOutputTitle: {
                default: 'conforming limit',
            },
            secondOutputTitle: {
                default: 'fha limit',
            },
            state: {
                default: '',
            },
            countryLabel: {
                default: 'COUNTY',
            },
            county: {
                default: '',
            },
            countyOptionTitle: {
                default: 'Choose your county',
            },
            conformingLimit: {
                default: '',
            },
            fhaLimit: {
                default: '',
            },
        },
        data() {
            return {
                State: '',
                StateList: States.StateList,
                CountyList: null,
                County: this.county || null,
                CountyOptionTitle: this.countyOptionTitle,
                ConformingLimit: this.conformingLimit,
                FhaLimit: this.fhaLimit,
            };
        },
        watch: {
            async State(newState, oldState) {
                if (newState) {
                    // If state changed (user selection), reset county
                    if (oldState && oldState !== newState) {
                        this.County = null;
                    }

                    await this.getCounties(newState);

                    // Only set County if it's not already set (to avoid overriding county from zipCode)
                    if (!this.County && this.CountyList && this.CountyList.length > 0) {
                        this.County = this.CountyList[0].value;
                    }
                }
            },
            async County(county) {
                if (county) {
                    await this.getLimit(county);
                }
            },
        },
        mounted() {
            this.createGraphQlClient(getFinancialApiUrl(), setHeader);
            this.getDefaultLocationByIp();
        },
        methods: {
            async getDefaultLocationByIp() {
                // If state prop provided, just set it and let the watcher handle the rest
                if (this.state) {
                    this.State = this.state;
                    // If county prop also provided, manually load limits (watcher won't trigger on initial value)
                    if (this.County) {
                        await this.$nextTick();
                        await this.getLimit(this.County);
                    }
                    return;
                }

                // Otherwise, get location from IP
                try {
                    const result = await GetIpAddress();

                    // If we have a zipCode, set county first before setting state
                    if (result.zipCode) {
                        this.State = result.state;
                        await this.$nextTick();
                        await this.getCounty(result.zipCode);
                    } else {
                        // No zipCode, just set state and let watcher set county to first in list
                        this.State = result.state;
                    }
                } catch (e) {
                    console.error(e);
                    this.StateList.unshift({ text: 'Choose Your State', value: '' });
                }
            },
            async getCounty(zip) {
                try {
                    const result = await GetZipCode(zip);
                    this.County = result.county;
                } catch (e) {
                    console.warn(`Error getting county from zip code: ${e}`);
                }
            },
            async getCounties(state) {
                const variables = {
                    state,
                    titleCase: true,
                };

                try {
                    const data = await this.graphQlQuery(CountiesFromState, variables);
                    const { result } = data;
                    // Remove duplicates using Set
                    const uniqueCounties = [...new Set(result)];
                    this.CountyList = uniqueCounties.map(county => ({
                        text: county,
                        value: county,
                    }));
                } catch (e) {
                    console.warn(`Error getting counties: ${e}`);
                }
            },
            async getLimit(county) {
                const variables = {
                    county,
                    state: this.State,
                };
                try {
                    const data = await this.graphQlQuery(LimitsForCounty, variables);
                    const { result } = data;

                    this.FhaLimit = this.formatMoney(result[0].limit1LivingUnit);
                    this.ConformingLimit = this.formatMoney(result[1].limit1LivingUnit);
                } catch (e) {
                    console.warn(`Error getting county loan limits: ${e}`);
                }
            },
            formatMoney(val) {
                return val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).split('.')[0];
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
